import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSliderModule } from '@angular/material/slider';
import { TranslateModule } from '@ngx-translate/core';
import { ColorPickerModule } from '@telespot/shared/color-picker';

import { LabelUsageChecklistRow } from '../../../state/label-config-multi-assign.state';
import { MethodTypeIconComponent } from '../../method-types/method-type-icon/method-type-icon.component';

import { LabelConfigMultiAssignFacade } from './label-config-multi-assign.facade';

/**
 * Assigns ONE colour to any number of the method types that use a label.
 *
 * Smart component: the whole form (selection, colour, alpha) lives in the
 * session state behind `LabelConfigMultiAssignFacade`, which the dialog host
 * provides — hence no `@Input`/`@Output` at all. Submitting stages the rows
 * into the shared ledger and ends the session through the facade's outcome
 * stream; the host is what actually closes the dialog.
 */
@Component({
  selector: 'ts-label-config-editor',
  templateUrl: './label-config-editor.component.html',
  styleUrls: ['./label-config-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatSliderModule,
    MatProgressSpinnerModule,
    ColorPickerModule,
    MethodTypeIconComponent,
  ],
})
export class LabelConfigEditorComponent {
  constructor(public readonly facade: LabelConfigMultiAssignFacade) {}

  public trackByMethodTypeId(_: number, row: LabelUsageChecklistRow): string {
    return row.methodTypeId;
  }

  public onColorPicked(color: string | null): void {
    this.facade.setColor(color);
  }

  public onAlphaPicked(alpha: number): void {
    this.facade.setAlpha(alpha);
  }

  public onRowToggled(row: LabelUsageChecklistRow): void {
    this.facade.toggleRow(row.methodTypeId);
  }

  public onAllToggled(): void {
    this.facade.toggleAll();
  }

  public onSubmit(): void {
    this.facade.submit();
  }

  public onDismiss(): void {
    this.facade.dismiss();
  }
}
